import {
	closestCenter,
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Cookies from "js-cookie";
import {
	GripVertical,
	Import,
	Menu as MenuIcon,
	Plus,
	Search,
	Trash2,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { _axios } from "../../../../../api";
import { createSlug, Menu } from "../../../../../features/addMenu";
import { adminTokenCookieName } from "../../../../../utils/constants";
import { HeaderAction } from "../../types";

// Type definitions
interface MenuItem {
	id: string;
	text: string;
	link: string;
}

interface MenuData {
	id: string;
	name: string;
	slug: string;
	items: MenuItem[];
}

interface ComponentSettings {
	items?: MenuItem[];
	menuSlug?: string;
}

interface Component {
	id: string;
	settings?: ComponentSettings;
}

interface EnhancedMenuEditorProps {
	component: Component;
	dispatch: React.Dispatch<HeaderAction>;
}

interface SortableItemProps {
	item: MenuItem;
	index: number;
	onUpdate: (index: number, field: keyof MenuItem, value: string) => void;
	onDelete: (index: number) => void;
}

// Sortable Item Component
const SortableItem: React.FC<SortableItemProps> = ({
	item,
	index,
	onUpdate,
	onDelete,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`flex items-center gap-2 mb-2 p-2 border rounded ${
				isDragging ? "bg-gray-50" : "bg-white"
			}`}>
			<div
				{...attributes}
				{...listeners}
				className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600">
				<GripVertical size={16} />
			</div>

			<input
				type="text"
				className="p-2 border rounded flex-1"
				value={item.text}
				placeholder="Label"
				onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
					onUpdate(index, "text", e.target.value)
				}
			/>

			<input
				type="text"
				className="p-2 border rounded flex-1"
				value={item.link}
				placeholder="URL"
				onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
					onUpdate(index, "link", e.target.value)
				}
			/>

			<button
				className="p-2 text-red-500 hover:text-red-700"
				onClick={() => onDelete(index)}>
				<Trash2 size={16} />
			</button>
		</div>
	);
};

// Main Menu Editor Component
const EnhancedMenuEditor: React.FC<EnhancedMenuEditorProps> = ({
	component,
	dispatch,
}) => {
	const [menuData, setMenuData] = useState<Menu[]>([]);
	const [activeTab, setActiveTab] = useState<"custom" | "import">("custom");
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selectedMenu, setSelectedMenu] = useState<Menu | null>(null);

	useEffect(() => {
		const fetchMenus = async () => {
			const res = await _axios.get(
				process.env.REACT_APP_BUILDER_URL + "menu",
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			const data = res.data;
			setMenuData(data);
		};

		fetchMenus();
	}, []);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	// Filter menu data based on search term
	const filteredMenuData = useMemo(() => {
		if (!searchTerm) return menuData;
		return menuData.filter(
			(menu) =>
				menu.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				menu.slug.toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [searchTerm, menuData]);

	// Handle drag end for custom items
	const handleDragEnd = (event: DragEndEvent): void => {
		const { active, over } = event;

		if (active.id !== over?.id) {
			const items = component.settings?.items || [];
			const oldIndex = items.findIndex(
				(item: MenuItem) => item.id === active.id
			);
			const newIndex = items.findIndex(
				(item: MenuItem) => item.id === over?.id
			);

			const newItems = arrayMove(items, oldIndex, newIndex);

			dispatch({
				type: "UPDATE_COMPONENT",
				payload: {
					id: component.id,
					updates: {
						settings: {
							...component.settings,
							items: newItems,
						},
					},
				},
			});
		}
	};

	// Update custom menu item
	const updateCustomItem = (
		index: number,
		field: keyof MenuItem,
		value: string
	): void => {
		const updatedItems = [...(component.settings?.items || [])];
		updatedItems[index] = {
			...updatedItems[index],
			[field]: value,
		};

		dispatch({
			type: "UPDATE_COMPONENT",
			payload: {
				id: component.id,
				updates: {
					settings: {
						...component.settings,
						items: updatedItems,
					},
				},
			},
		});
	};

	// Delete custom menu item
	const deleteCustomItem = (index: number): void => {
		const updatedItems = (component.settings?.items || []).filter(
			(_: MenuItem, i: number) => i !== index
		);

		dispatch({
			type: "UPDATE_COMPONENT",
			payload: {
				id: component.id,
				updates: {
					settings: {
						...component.settings,
						items: updatedItems,
					},
				},
			},
		});
	};

	// Add new custom item
	const addCustomItem = (): void => {
		const newItem: MenuItem = {
			id: `nav-item-${Date.now()}`,
			text: "New Item",
			link: "/",
		};

		dispatch({
			type: "UPDATE_COMPONENT",
			payload: {
				id: component.id,
				updates: {
					settings: {
						...component.settings,
						items: [...(component.settings?.items || []), newItem],
					},
				},
			},
		});
	};

	// Import selected menu
	const importMenu = (menu: Menu): void => {
		dispatch({
			type: "UPDATE_COMPONENT",
			payload: {
				id: component.id,
				updates: {
					settings: {
						...component.settings,
						items:
							menu.menu?.map((item, index) => ({
								id: index + 1 + "",
								text: item.name,
								link: "/" + item.url || "/",
							})) || [],
						menuSlug: createSlug(menu.name),
					},
				},
			},
		});
		setSelectedMenu(menu);
		setActiveTab("custom"); // Switch back to custom tab after import
	};

	// Update menu slug
	const updateMenuSlug = (slug: string): void => {
		dispatch({
			type: "UPDATE_COMPONENT",
			payload: {
				id: component.id,
				updates: {
					settings: {
						...component.settings,
						menuSlug: slug,
					},
				},
			},
		});
	};

	return (
		<div className="mb-4">
			{/* Menu Slug Field */}
			<div className="mb-4">
				<label className="block text-sm font-medium mb-2">
					Menu Slug
				</label>
				<input
					type="text"
					className="w-full p-2 border rounded"
					value={component.settings?.menuSlug || ""}
					placeholder="menu-slug"
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						updateMenuSlug(e.target.value)
					}
				/>
			</div>

			{/* Tabs */}
			<div className="border-b border-gray-200 mb-4">
				<nav className="-mb-px flex space-x-8">
					<button
						onClick={() => setActiveTab("custom")}
						className={`py-2 px-1 border-b-2 font-medium text-sm ${
							activeTab === "custom"
								? "border-blue-500 text-blue-600"
								: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
						}`}>
						Custom Menu
					</button>
					<button
						onClick={() => setActiveTab("import")}
						className={`py-2 px-1 border-b-2 font-medium text-sm ${
							activeTab === "import"
								? "border-blue-500 text-blue-600"
								: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
						}`}>
						Import Menu
					</button>
				</nav>
			</div>

			{/* Tab Content */}
			{activeTab === "custom" && (
				<div>
					<label className="block text-sm font-medium mb-2">
						Menu Items
					</label>

					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}>
						<SortableContext
							items={(component.settings?.items || []).map(
								(item: MenuItem) => item.id
							)}
							strategy={verticalListSortingStrategy}>
							{(component.settings?.items || []).map(
								(item: MenuItem, index: number) => (
									<SortableItem
										key={item.id}
										item={item}
										index={index}
										onUpdate={updateCustomItem}
										onDelete={deleteCustomItem}
									/>
								)
							)}
						</SortableContext>
					</DndContext>

					<button
						className="flex items-center mt-2 px-3 py-2 border rounded bg-blue-50 hover:bg-blue-100 text-blue-600"
						onClick={addCustomItem}>
						<Plus size={16} />
						<span className="ml-1">Add Item</span>
					</button>
				</div>
			)}

			{activeTab === "import" && (
				<div>
					<label className="block text-sm font-medium mb-2">
						Import from Menu Data
					</label>

					{/* Search */}
					<div className="relative mb-4">
						<Search
							className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
							size={16}
						/>
						<input
							type="text"
							className="w-full pl-10 pr-4 py-2 border rounded"
							placeholder="Search menus..."
							value={searchTerm}
							onChange={(
								e: React.ChangeEvent<HTMLInputElement>
							) => setSearchTerm(e.target.value)}
						/>
					</div>

					{/* Menu List */}
					<div className="space-y-2 max-h-64 overflow-y-auto">
						{filteredMenuData.map((menu) => (
							<div
								key={menu._id}
								className="p-3 border rounded hover:bg-gray-50 cursor-pointer"
								onClick={() => importMenu(menu)}>
								<div className="flex items-center justify-between">
									<div>
										<h4 className="font-medium text-gray-900">
											{menu.name}
										</h4>
										<p className="text-sm text-gray-500">
											Slug: {menu.slug}
										</p>
										<p className="text-xs text-gray-400">
											{menu.menu?.length} items
										</p>
									</div>
									<Import
										className="text-blue-500"
										size={16}
									/>
								</div>

								{/* Preview items */}
								<div className="mt-2 text-xs text-gray-500">
									Items:{" "}
									{menu.menu
										?.map((item) => item.name)
										.join(", ")}
								</div>
							</div>
						))}
					</div>

					{filteredMenuData.length === 0 && (
						<div className="text-center py-8 text-gray-500">
							<MenuIcon
								size={48}
								className="mx-auto mb-2 opacity-50"
							/>
							<p>No menus found</p>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default EnhancedMenuEditor;
